import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { User, UserProfile } from '@/graphql/mutations/login';
import { useQuery } from '@apollo/client/react';
import { MERCHANT_QUERY, MerchantResponse } from '@/graphql/queries/merchants';
import { apolloClientV2 } from '@/lib/apollo-client-v2';
import { clearAuthStorage, REFRESH_TOKEN_KEY, TOKEN_KEY, USER_KEY } from '@/lib/auth-session';

type EnrichedProfile = UserProfile & { merchantCode?: string | null };
type EnrichedUser = Omit<User, 'profile'> & { profile?: EnrichedProfile | null };

interface AuthContextType {
  user: EnrichedUser | null;
  isAuthenticated: boolean;
  login: (user: User, token: string, refreshToken: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<EnrichedUser | null>(() => {
    const storedUser = localStorage.getItem(USER_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));

  const userMerchantId = user?.profile?.merchantId ? Number(user.profile.merchantId) : null;
  const hasMerchantCode = !!user?.profile?.merchantCode;

  const { data: merchantData } = useQuery<MerchantResponse>(MERCHANT_QUERY, {
    variables: { id: userMerchantId },
    skip: !userMerchantId || hasMerchantCode || !user,
    fetchPolicy: 'cache-first',
    client: apolloClientV2,
  });

  const resolvedUser = useMemo<EnrichedUser | null>(() => {
    if (!user) return null;
    const merchantCode = user.profile?.merchantCode ?? merchantData?.merchant?.merchantCode;
    if (merchantCode && merchantCode !== user.profile?.merchantCode) {
      return {
        ...user,
        profile: {
          ...user.profile,
          merchantCode,
        },
      };
    }
    return user;
  }, [user, merchantData?.merchant?.merchantCode]);

  useEffect(() => {
    if (!resolvedUser || !merchantData?.merchant?.merchantCode || user?.profile?.merchantCode) {
      return;
    }
    localStorage.setItem(USER_KEY, JSON.stringify(resolvedUser));
  }, [resolvedUser, merchantData?.merchant?.merchantCode, user?.profile?.merchantCode]);

  const login = useCallback((userData: User, authToken: string, authRefreshToken: string) => {
    localStorage.setItem(TOKEN_KEY, authToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, authRefreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
    setToken(authToken);
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setToken(null);
  }, []);

  const isAuthenticated = !!token && !!user;

  const contextValue = useMemo<AuthContextType>(
    () => ({ user: resolvedUser, isAuthenticated, login, logout }),
    [resolvedUser, isAuthenticated, login, logout],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
